import { memo, useMemo } from 'react'
import { twMerge } from 'tailwind-merge'
import { AudioNote as AudioNoteType } from '@highlight/common'
import type { ChatAttachment, FinalizedAttachment } from '../../../../types'
import { LIVE_TRANSCRIPT_PENDING_KEY, parseLiveTranscriptText } from '../../../../utils/audioNotes'
import { FadingScrollable } from '../../../elements/FadingScrollable'
import { LoadingText } from '../../../elements/LoadingText'
import { AudioNoteIcon, Timestamp, useAudioNoteTitle } from '../../input/context/audio/audio-elements'
import { PreviewableContext } from '../../input/context/previewable-context'
import { AttachmentStub } from '../attachment-stub'
import { useRemoveAttachment } from '../hooks/use-remove-attachment'
import { scrollableAreaClassName } from '../styles'
import type { AttachmentPillProps } from '../types'
import { LiveTranscriptChip, SentLiveTranscript } from './live-transcript-attachment'

export const AudioNote = memo((attachment: AttachmentPillProps) => {
  const audioNote = attachment.attachment as Extract<FinalizedAttachment, { type: 'audioNote' }>
  const conversation = attachment.metadata as AudioNoteType
  const onRemove = useRemoveAttachment(attachment)

  // Always call hooks at the top level - useAudioNoteTitle should handle null/undefined
  const conversationTitle = useAudioNoteTitle(conversation)

  // A recording meeting: its chip on the composer, or what one send of it carried
  if (attachment.metadata?.[LIVE_TRANSCRIPT_PENDING_KEY] && audioNote?.type === 'audioNote') {
    return onRemove ? (
      <LiveTranscriptChip
        title={conversation.title ?? audioNote.title ?? 'Meeting'}
        startTime={audioNote.startTime}
        onRemove={onRemove}
      />
    ) : (
      <SentLiveTranscript />
    )
  }
  const liveSend = audioNote?.type === 'audioNote' ? parseLiveTranscriptText(audioNote.text) : null
  if (liveSend) {
    return <SentLiveTranscript kind={liveSend.kind} transcript={liveSend.transcript} />
  }

  // Handle pending audio note (loading state)
  if (attachment.pending?.type === 'audioNote') {
    return (
      <AttachmentStub
        label={<LoadingText text="Loading transcript..." className="text-foreground-placeholder" />}
        icon={<AudioNoteIcon size={16} {...conversation} />}
        onRemove={onRemove}
      />
    )
  }

  // Handle finalized audio note
  if (attachment.attachment?.type === 'audioNote') {
    return (
      <PreviewableContext preview={<AudioPreview {...attachment} />}>
        <AttachmentStub
          label={audioNote?.title ?? conversationTitle ?? 'Audio Note'}
          icon={<AudioNoteIcon size={16} {...conversation} />}
          onRemove={onRemove}
        />
      </PreviewableContext>
    )
  }

  // Fallback for malformed attachment
  return <AttachmentStub label="Audio Note" icon={<AudioNoteIcon size={16} />} onRemove={onRemove} />
})
AudioNote.displayName = 'AudioNote'

const AudioPreview = memo((attachment: ChatAttachment) => {
  const audioNote = attachment.attachment as Extract<FinalizedAttachment, { type: 'audioNote' }>
  const conversation = attachment.metadata as AudioNoteType
  const title = useAudioNoteTitle(conversation)

  const transcript = useMemo(() => {
    return (audioNote?.text ?? conversation?.transcript ?? '')
      .split('\n')
      .reverse()
      .map((string, line) => {
        if (string.includes('other(s):') || string.includes('self:') || string.includes('other speaker:')) {
          const regex = string.includes('self:')
            ? /self:/
            : string.includes('other speaker:')
              ? /other speaker:/
              : /other\(s\):/
          const identifier = string.match(regex)?.[0] ?? ''
          const [speaker, text] = string.split(identifier)
          return (
            <div className={'font-350 flex flex-col'} key={`notes-line-${line}`}>
              <span className={twMerge('text-white/30')}>{speaker + identifier}</span>
              <span className={'leading-tight'}>{text}</span>
            </div>
          )
        }
        return <span key={`notes-line-${line}`}>{string}</span>
      })
  }, [conversation?.transcript, audioNote?.text])

  return (
    <div className={'flex h-48 max-h-48 max-w-64 flex-col gap-3 p-4 pb-0 pt-3.5 text-[13px]'}>
      <div className={'flex flex-col gap-0.5 font-normal'}>
        <span className={'truncate'}>{audioNote?.title ?? title}</span>
        <span>
          <Timestamp date={audioNote?.startTime ?? conversation?.startedAt} />
        </span>
      </div>
      <FadingScrollable className={scrollableAreaClassName} onClick={(e) => e.stopPropagation()} fadeSize="20%">
        {transcript}
      </FadingScrollable>
    </div>
  )
})
AudioPreview.displayName = 'AudioPreview'
